<template>
  <base-modal
    :ref="modal.key"
    :show="open"
    :title="modal.title"
    @on-confirm="save()"
    @on-cancel="hide()"
    maxWidth="70%"
    :okText="$t('Save')"
    :closeText="$t('Close')"
  >
    <v-row justify="center" align="center" v-if="loading">
      <div class="text-center">
        <v-progress-circular
          :size="50"
          color="primary"
          indeterminate
        ></v-progress-circular>
      </div>
    </v-row>

    <v-row justify="center" align="center" v-if="!loading">
      <v-col class="pl-12">
        <v-text-field
          v-model="emailCredentials.emailAccount"
          :label="$t('Account')"
          :error-messages="emailAccountErrors"
          @input="$v.emailCredentials.emailAccount.$touch()"
          @blur="$v.emailCredentials.emailAccount.$touch()"
        ></v-text-field>
      </v-col>
      <v-col>
        <v-text-field
          v-model="emailCredentials.emailPassword"
          :append-icon="showPassword ? 'mdi-eye' : 'mdi-eye-off'"
          @click:append="showPassword = !showPassword"
          :type="showPassword ? 'text' : 'password'"
          :label="$t('Password')"
          :error-messages="emailPasswordErrors"
          @input="$v.emailCredentials.emailPassword.$touch()"
          @blur="$v.emailCredentials.emailPassword.$touch()"
        ></v-text-field>
      </v-col>
      <v-col>
        <v-text-field
          v-model="emailCredentials.emailServer"
          :label="$t('Server')"
          :error-messages="emailServerErrors"
          @input="$v.emailCredentials.emailServer.$touch()"
          @blur="$v.emailCredentials.emailServer.$touch()"
        ></v-text-field>
      </v-col>
      <v-col>
        <v-text-field
          v-model="emailCredentials.emailPort"
          :label="$t('Port')"
          :error-messages="emailPortErrors"
          @input="$v.emailCredentials.emailPort.$touch()"
          @blur="$v.emailCredentials.emailPort.$touch()"
          type="number"
        ></v-text-field>
      </v-col>
      <v-col>
        <v-switch
          v-model="emailCredentials.emailUseSSL"
          :label="$t('UseSSL')"
          false-value="false"
          true-value="true"
        ></v-switch>
      </v-col>
      <v-col cols="1" align-self="center">
        <v-tooltip top>
          <template v-slot:activator="{ on }">
            <v-btn icon dark color="primary" @click="checkMail()" v-on="on" :loading="loading">
              <v-icon dark>mdi-checkbox-marked-circle-outline</v-icon>
            </v-btn>
          </template>
          <span>{{ $t("CheckEmail") }}</span>
        </v-tooltip>
      </v-col>
    </v-row>
  </base-modal>
</template>
<script>
import Utils from '@/ecm/common/Utils';
import AutoImportService from '@/services/AutoImportService';
import { validationMixin } from 'vuelidate';
import { required, minValue, integer } from 'vuelidate/lib/validators';

export default {
    data() {
        return {
            open: false,
            loading: false,
            uuid: 0,
            emailCredentials: {
                emailAccount: '',
                emailPassword: '',
                emailServer: '',
                emailPort: 0,
                emailUseSSL: false
            },
            showPassword: false,
            modal: {
                title: this.$t('EmailCredentials'),
                key: 'userModal'
            }
        };
    },
    mixins: [validationMixin],
    validations: {
        emailCredentials: {
            emailAccount: { required },
            emailPassword: { required },
            emailServer: { required },
            emailPort: { required, minValue: minValue(1), integer }
        }
    },
    computed: {
        emailAccountErrors() {
            const errors = [];
            if (!this.$v.emailCredentials.emailAccount.$dirty) return errors;
            !this.$v.emailCredentials.emailAccount.required &&
                errors.push(this.$t('RequiredField'));
            return errors;
        },
        emailPasswordErrors() {
            const errors = [];
            if (!this.$v.emailCredentials.emailPassword.$dirty) return errors;
            !this.$v.emailCredentials.emailPassword.required &&
                errors.push(this.$t('RequiredField'));
            return errors;
        },
        emailServerErrors() {
            const errors = [];
            if (!this.$v.emailCredentials.emailServer.$dirty) return errors;
            !this.$v.emailCredentials.emailServer.required &&
                errors.push(this.$t('RequiredField'));
            return errors;
        },
        emailPortErrors() {
            const errors = [];
            if (!this.$v.emailCredentials.emailPort.$dirty) return errors;
            !this.$v.emailCredentials.emailPort.required &&
                errors.push(this.$t('RequiredField'));
            !this.$v.emailCredentials.emailPort.minValue &&
                errors.push(`${this.$t('MinValue')}: 1`);
            !this.$v.emailCredentials.emailPort.integer &&
                errors.push(this.$t('IntegerValues'));
            return errors;
        },
        modalInstance() {
            return this.$refs[this.modal.key];
        }
    },
    methods: {
        show(item) {
            this.open = true;
            this.emailCredentials.emailAccount = item.emailAccount;
            this.emailCredentials.emailServer = item.emailServer;
            this.emailCredentials.emailPort = item.emailPort;
            this.emailCredentials.emailUseSSL = item.emailUseSSL;
        },
        hide() {
            this.open = false;
            this.loading = false;
        },
        save() {
            this.$v.$touch();
            this.loading = true;
            if (this.$v.$invalid) {
                this.loading = false;
                return;
            }
            this.modalInstance.progress(true);
            AutoImportService.checkEmailCredential(this.emailCredentials)
                .then(data => {
                    this.hide();
                    this.$emit('emailConfigurationSaved', this.emailCredentials);
                    this.loading = false;
                })
                .catch(() => {
                    // error
                })
                .then(() => {
                    //always
                    this.loading = false;
                    this.modalInstance.progress(false);
                });
        },
        checkMail() {
            this.$v.$touch();
            if (this.$v.$invalid) {
                return;
            }
            this.loading = true;
            AutoImportService.checkEmailCredential(this.emailCredentials)
                .then(data => {
                    Utils.toast.success(this.$t('MailCredentialValid'));
                })
                .catch(() => {
                    // error
                })
                .then(() => {
                    //always
                    this.loading = false;
                });
        }
    }
};
</script>
